import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, AlertTitle, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, LinearProgress, Table,
    TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { observer } from 'mobx-react-lite';
import bulkAudioStore from '../../Mobx/BulkAudioStore';
import { DICTIONARY_INFO, FIELD_INFO, formatError } from '../../Mobx/AudioCatalog';
import {
    ERROR_KIND_LABELS, GENDER_LABELS, ITEM_STATUSES, MODE_LABELS, PAUSE_REASON_TEXT, PROVIDER_LABELS, PROVIDER_SHORT_LABELS, backupFolderOf, counterLabels, describeGroupStats,
    describeItem, describeJob, describeUsage, describeVoices, formatDateTime, formatNumber, formatUsd, groupSizeOf, groupsPrefixOf, isGroupRun, isReplaceMode, isTrialMode,
    itemStatusLabels, languageLabel, limitsOf, modeOf, progressOf, trialFolderOf, PATH_STYLE
} from './bulkAudioForm';
import { ConfirmDialog, chipColor } from './BulkRunJobs';
import BulkRunGroupRecordings from './BulkRunGroupRecordings';

const ROWS_PER_PAGE = 50;
const REFRESH_MS = 3000;
const ITEM_COLORS = { pending: 'default', working: 'info', done: 'success', skipped: 'default', failed: 'error' };

const Stat = ({ label, value, testId, color }) => (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, minWidth: 110 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" color={color} data-testid={testId}>{value}</Typography>
    </Box>
);

/** A line of the summary: what, and its value */
const Line = ({ label, children }) => (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', py: 0.25 }}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>{label}</Typography>
        <Typography variant="body2" dir="auto" component="div">{children}</Typography>
    </Box>
);

/** What a run was asked to do, in a few lines */
const ConfigSummary = ({ job, options }) => {
    const config = job.config ?? {};
    const mode = modeOf(job);
    const source = config.source?.type === 'ids'
        ? `${formatNumber(config.source.count)} מזהי ערכים`
        : `סינון ${JSON.stringify(config.source?.filter ?? {})}, עד ${formatNumber(config.source?.limit)} ערכים${config.source?.afterId ? `, אחרי ${config.source.afterId}` : ''}`;
    return (
        <Box sx={{ mt: 2 }}>
            <Line label="מצב">{MODE_LABELS[mode] ?? mode}{groupSizeOf(job) !== null ? ` · בקבוצות של עד ${formatNumber(groupSizeOf(job))} מילים` : ''}</Line>
            {isReplaceMode(mode) && <Line label="גיבוי"><bdi dir="ltr" style={PATH_STYLE} data-testid="bulk-backup-folder">{backupFolderOf(job, options)}</bdi></Line>}
            {isTrialMode(mode) && <Line label="תיקיית הניסוי"><bdi dir="ltr" style={PATH_STYLE} data-testid="bulk-trial-folder">{trialFolderOf(job, options)}</bdi></Line>}
            {isGroupRun(job) && <Line label="הקלטות הקבוצות"><bdi dir="ltr" style={PATH_STYLE} data-testid="bulk-groups-folder">{`${groupsPrefixOf(options)}${job.id}/`}</bdi></Line>}
            <Line label="מילון">{DICTIONARY_INFO[config.dictionary]?.label ?? config.dictionary}</Line>
            <Line label="ערכים"><span dir="auto">{source}</span></Line>
            <Line label="מה הוקרא">
                {(config.fields ?? []).map((field) => FIELD_INFO[field] ?? field).join(', ')} · {(config.languages ?? []).map(languageLabel).join(', ')} · {(config.genders ?? []).map((gender) => GENDER_LABELS[gender] ?? gender).join(', ')}
            </Line>
            <Line label="ספק">{PROVIDER_LABELS[config.provider] ?? config.provider}{config.model ? ` · ${config.model}` : ''} · {describeVoices(config.voices)}</Line>
            {config.fallback && (
                <Line label="גיבוי">
                    {PROVIDER_LABELS[config.fallback.provider] ?? config.fallback.provider}{config.fallback.model ? ` · ${config.fallback.model}` : ''} · {describeVoices(config.fallback.voices)}
                    {job.switchedToFallback ? ' · ההרצה עברה לגיבוי' : ''}
                </Line>
            )}
            <Line label="קצב">{formatNumber(config.rpm)} בקשות בדקה, {formatNumber(config.concurrency)} במקביל</Line>
            <Line label="עלות"><bdi>{formatUsd(job.estimatedCostUsd)}</bdi> (הערכה) מתוך תקרה של <bdi>{formatUsd(job.maxCostUsd)}</bdi></Line>
            {describeUsage(job.usage) && <Line label="נוצר בפועל"><bdi>{describeUsage(job.usage)}</bdi></Line>}
            {describeGroupStats(job.groupStats).length > 0 && (
                <Line label="קבוצות">
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }} data-testid="bulk-group-stats">
                        {describeGroupStats(job.groupStats).map((row) => (
                            <Chip key={row.key} size="small" variant="outlined" color={chipColor(row.severity)} label={`${row.label}: ${row.value}`} data-stat={row.key} />
                        ))}
                    </Box>
                </Line>
            )}
            <Line label="הופעלה על ידי"><bdi>{job.createdBy || ''}</bdi> · <bdi>{formatDateTime(job.startedAt ?? job.createdAt)}</bdi>{job.finishedAt ? <> · הסתיימה <bdi>{formatDateTime(job.finishedAt)}</bdi></> : null}</Line>
        </Box>
    );
};

/**
 * One run: its state, its numbers, its files (with the error of every file that failed), and the stop / resume buttons.
 * It refreshes itself every few seconds while it is open.
 */
const BulkRunJobDetails = observer(({ jobId, options, onClose }) => {
    const store = bulkAudioStore;
    const job = store.jobs?.find((item) => item.id === jobId) ?? null;
    const { actionError, actionJobId } = store;
    const limits = limitsOf(options);

    const [loadError, setLoadError] = useState(null);
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(0);
    const [files, setFiles] = useState({ items: [], total: 0, error: null });
    const [retryFailed, setRetryFailed] = useState(false);
    const [newCap, setNewCap] = useState('');
    const [confirming, setConfirming] = useState(null); // 'stop' | 'resume' | null
    const [preview, setPreview] = useState(null); // a file of a trial that is being heard: { seq, text, fileName, url }
    const [previewError, setPreviewError] = useState(false);

    // The files that an answer holds are shown only if they are the files of the filter and the page that are open now:
    // a slow answer for the page that the admin has just left must not replace the page that he sees
    const openRef = useRef({ filter, page });
    openRef.current = { filter, page };

    const refresh = useCallback(async () => {
        const [fetched, list] = await Promise.all([
            store.fetchJob(jobId),
            store.fetchItems(jobId, { status: filter, skip: page * ROWS_PER_PAGE, limit: ROWS_PER_PAGE }),
        ]);
        setLoadError(fetched.error);
        if (openRef.current.filter !== filter || openRef.current.page !== page) return;
        setFiles({ items: list.items, total: list.total, error: list.error });
    }, [store, jobId, filter, page]);

    useEffect(() => {
        refresh();
        const timer = setInterval(refresh, REFRESH_MS);
        return () => clearInterval(timer);
    }, [refresh]);

    const status = describeJob(job);
    const progress = progressOf(job);
    const counters = job?.counters ?? {};
    const mode = modeOf(job);
    const replacing = isReplaceMode(mode);
    const trial = isTrialMode(mode);
    const countLabels = counterLabels(mode);
    const itemLabels = itemStatusLabels(mode);
    const headers = ['#', ...(trial ? ['האזנה'] : []), 'מצב', 'שפה', 'מגדר', 'טקסט', 'שם הקובץ', 'קול', 'ניסיונות', 'שגיאה'];
    // The link of a file is valid for an hour and is asked for again with every refresh: the player keeps the one it started with
    const listen = (item) => {
        setPreviewError(false);
        setPreview({ seq: item.seq, text: item.text, fileName: item.fileName, url: item.previewUrl });
    };

    const capValue = newCap.trim() === '' ? undefined : Number(newCap.trim());
    const capValid = capValue === undefined || (Number.isFinite(capValue) && capValue > 0 && capValue <= limits.maxCostUsd);
    const mustRetryFailed = job?.status === 'completed';
    const resumeBody = {
        ...(mustRetryFailed || retryFailed ? { retryFailed: true } : {}),
        ...(capValue !== undefined ? { maxCostUsd: capValue } : {}),
    };

    const act = async () => {
        const result = confirming === 'stop' ? await store.stopJob(jobId) : await store.resumeJob(jobId, resumeBody);
        if (result) {
            setConfirming(null);
            setRetryFailed(false);
            setNewCap('');
            refresh();
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                פרטי הרצה
                {job && <Chip size="small" color={chipColor(status.severity)} label={status.label} data-testid="bulk-detail-status" />}
            </DialogTitle>
            <DialogContent dividers data-testid="bulk-details">
                {loadError && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        <AlertTitle>טעינת ההרצה נכשלה</AlertTitle>
                        <Box dir="auto">{formatError(loadError)}</Box>
                    </Alert>
                )}
                {!job && !loadError && <LinearProgress />}

                {job && (
                    <>
                        {job.pauseReason && PAUSE_REASON_TEXT[job.pauseReason] && (
                            <Alert severity="warning" sx={{ mb: 1 }} data-testid="bulk-pause-reason">{PAUSE_REASON_TEXT[job.pauseReason]}</Alert>
                        )}
                        {job.stalled && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                                ההרצה לא עדכנה את השרת כבר זמן מה (ייתכן שהשרת הופעל מחדש). אפשר להמשיך אותה: הקבצים שהיו בעבודה יחזרו להמתנה.
                            </Alert>
                        )}
                        {job.message && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="bulk-detail-message">
                                הודעת השרת: <bdi>{job.message}</bdi>
                            </Typography>
                        )}

                        <LinearProgress variant="determinate" value={progress.percent} sx={{ height: 8, borderRadius: 1, mb: 1 }} aria-label="התקדמות ההרצה" />
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Stat label="סה״כ קבצים" value={formatNumber(counters.total)} testId="bulk-detail-total" />
                            <Stat label={countLabels.done} value={formatNumber(counters.done)} testId="bulk-detail-done" />
                            <Stat label={countLabels.skipped} value={formatNumber(counters.skipped)} testId="bulk-detail-skipped" />
                            <Stat label="נכשלו" value={formatNumber(counters.failed)} testId="bulk-detail-failed" color={(counters.failed ?? 0) > 0 ? 'error' : undefined} />
                            <Stat label="ממתינים" value={formatNumber(counters.pending)} testId="bulk-detail-pending" />
                        </Box>

                        {replacing && (
                            <Alert severity="info" sx={{ mt: 2 }} data-testid="bulk-restore-help">
                                <AlertTitle>הקבצים הישנים נשמרו בגיבוי</AlertTitle>
                                כל קובץ שהוחלף הועתק קודם אל <bdi dir="ltr" style={PATH_STYLE}>{backupFolderOf(job, options)}</bdi>, באותו נתיב יחסי (למשל <bdi dir="ltr" style={PATH_STYLE}>he-IL/FEMALE/&lt;file&gt;.mp3</bdi>).
                                כדי לשחזר קובץ מעתיקים אותו מהגיבוי אל אותו נתיב תחת <bdi dir="ltr" style={PATH_STYLE}>files/audio/</bdi>. ב-S3 אין גרסאות, ולכן זו הדרך היחידה לחזור אחורה.
                                דפדפנים ששמרו את הקובץ החדש ימשיכו להשמיע אותו עד שהמטמון שלהם יפוג.
                            </Alert>
                        )}

                        <ConfigSummary job={job} options={options} />

                        {(job.canStop || job.canResume) && (
                            <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                {job.canStop && !job.stopRequested && (
                                    <Button color="warning" variant="outlined" onClick={() => { store.clearActionError(); setConfirming('stop'); }} data-testid="bulk-detail-stop">עצירת ההרצה</Button>
                                )}
                                {job.stopRequested && <Typography variant="body2">התבקשה עצירה: הקבצים שבעבודה מסתיימים.</Typography>}
                                {job.canResume && (
                                    <>
                                        {!mustRetryFailed && (counters.failed ?? 0) > 0 && (
                                            <FormControlLabel
                                                control={<Checkbox checked={retryFailed} onChange={() => setRetryFailed(!retryFailed)} inputProps={{ 'data-testid': 'bulk-retry-failed' }} />}
                                                label={`לנסות שוב גם את ${formatNumber(counters.failed)} הקבצים שנכשלו`}
                                            />
                                        )}
                                        <TextField
                                            size="small"
                                            label="תקרה חדשה (דולר)"
                                            value={newCap}
                                            onChange={(event) => setNewCap(event.target.value)}
                                            placeholder={String(job.maxCostUsd ?? '')}
                                            error={!capValid}
                                            helperText={capValid ? 'ריק: ללא שינוי' : `מספר מעל 0 ועד ${limits.maxCostUsd}`}
                                            inputProps={{ dir: 'ltr', inputMode: 'decimal', 'data-testid': 'bulk-new-cap' }}
                                            sx={{ width: 210 }}
                                        />
                                        <Button
                                            variant="contained"
                                            disabled={!capValid || (mustRetryFailed && (counters.failed ?? 0) === 0)}
                                            onClick={() => { store.clearActionError(); setConfirming('resume'); }}
                                            data-testid="bulk-detail-resume"
                                        >
                                            {mustRetryFailed ? 'ניסיון חוזר לקבצים שנכשלו' : 'המשך הרצה'}
                                        </Button>
                                    </>
                                )}
                            </Box>
                        )}

                        {isGroupRun(job) && <BulkRunGroupRecordings key={job.id} jobId={jobId} job={job} />}

                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 3, mb: 1 }}>הקבצים של ההרצה</Typography>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={filter}
                            onChange={(event, value) => { if (value !== null) { setFilter(value); setPage(0); } }}
                            aria-label="סינון הקבצים לפי מצב"
                            sx={{ mb: 1, flexWrap: 'wrap' }}
                        >
                            <ToggleButton value="" data-testid="bulk-items-all">הכל</ToggleButton>
                            {ITEM_STATUSES.map((itemStatus) => (
                                <ToggleButton key={itemStatus} value={itemStatus} data-testid={`bulk-items-${itemStatus}`}>{itemLabels[itemStatus]}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>

                        {files.error && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                                <AlertTitle>טעינת הקבצים נכשלה</AlertTitle>
                                <Box dir="auto">{formatError(files.error)}</Box>
                            </Alert>
                        )}
                        {!files.error && files.total === 0 && <Alert severity="info">אין קבצים במצב הזה.</Alert>}
                        {trial && preview && (
                            <Box sx={{ mb: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }} data-testid="bulk-preview">
                                <Typography variant="body2" dir="auto" sx={{ mb: 0.5 }}>
                                    מושמע: <b><bdi>{preview.text}</bdi></b> (<bdi>{preview.fileName}</bdi>)
                                </Typography>
                                <audio controls autoPlay src={preview.url} style={{ width: '100%' }} onError={() => setPreviewError(true)} data-testid="bulk-preview-audio" />
                                {previewError && <Alert severity="warning" sx={{ mt: 1 }} data-testid="bulk-preview-error">הקובץ לא נטען. ייתכן שהקישור פג (הוא תקף שעה אחת): לוחצים שוב על ההשמעה.</Alert>}
                            </Box>
                        )}
                        {files.items.length > 0 && (
                            <>
                                <TableContainer sx={{ maxHeight: 420 }}>
                                    <Table size="small" stickyHeader aria-label="הקבצים של ההרצה">
                                        <TableHead>
                                            <TableRow>
                                                {headers.map((header) => (
                                                    <TableCell key={header} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{header}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody data-testid="bulk-items-rows">
                                            {files.items.map((item) => (
                                                <TableRow key={item.seq} data-status={item.status}>
                                                    <TableCell>{item.seq}</TableCell>
                                                    {trial && (
                                                        <TableCell>
                                                            {item.previewUrl && (
                                                                <Tooltip title="האזנה לקובץ של הניסוי">
                                                                    <IconButton size="small" onClick={() => listen(item)} aria-label={`האזנה ל${item.text}`} data-testid={`bulk-listen-${item.seq}`}>
                                                                        <PlayArrowIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </TableCell>
                                                    )}
                                                    <TableCell><Chip size="small" color={ITEM_COLORS[item.status] ?? 'default'} label={describeItem(item, mode)} /></TableCell>
                                                    <TableCell>{languageLabel(item.language)}</TableCell>
                                                    <TableCell>{GENDER_LABELS[item.gender] ?? item.gender}</TableCell>
                                                    <TableCell><bdi>{item.text}</bdi></TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace' }}><bdi>{item.fileName}</bdi></TableCell>
                                                    <TableCell><bdi>{item.voice ? `${item.voice}${item.provider ? ` (${PROVIDER_SHORT_LABELS[item.provider] ?? item.provider})` : ''}` : ''}</bdi></TableCell>
                                                    <TableCell>{item.attempts || ''}</TableCell>
                                                    <TableCell sx={{ minWidth: 220 }}>
                                                        {item.error && (
                                                            <Box sx={{ color: 'error.main' }}>
                                                                {item.errorKind && <Box component="div" sx={{ fontWeight: 'bold' }}>{ERROR_KIND_LABELS[item.errorKind] ?? item.errorKind}</Box>}
                                                                <Box dir="auto">{item.error}</Box>
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    component="div"
                                    count={files.total}
                                    page={page}
                                    onPageChange={(event, next) => setPage(next)}
                                    rowsPerPage={ROWS_PER_PAGE}
                                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                                    labelDisplayedRows={({ from, to, count }) => `${formatNumber(from)}-${formatNumber(to)} מתוך ${formatNumber(count)}`}
                                />
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} data-testid="bulk-detail-close">סגירה</Button>
            </DialogActions>

            <ConfirmDialog
                open={confirming === 'stop'}
                title="לעצור את ההרצה?"
                confirmLabel="עצירה"
                busy={Boolean(actionJobId)}
                onCancel={() => setConfirming(null)}
                onConfirm={act}
                testId="bulk-detail-confirm-stop"
            >
                <Typography>
                    הקבצים שנמצאים בעבודה כרגע יסתיימו, ולא יתחילו קבצים נוספים.
                    {replacing ? ' הקבצים שכבר הוחלפו נשארים מוחלפים, והגיבוי שלהם נשמר.' : (trial ? ' הקבצים שכבר נוצרו נשארים בתיקיית הניסוי.' : ' הקבצים שכבר נוצרו נשארים.')}
                    {isGroupRun(job) ? ' בקשה של קבוצה שכבר נשלחה מסתיימת (היא כבר שולמה), ולכן העצירה יכולה לקחת כמה דקות.' : ''}
                    {' '}אפשר להמשיך את ההרצה אחר כך.
                </Typography>
                {actionError && <Alert severity="error" sx={{ mt: 1 }}><Box dir="auto">{formatError(actionError)}</Box></Alert>}
            </ConfirmDialog>

            <ConfirmDialog
                open={confirming === 'resume'}
                title={mustRetryFailed ? 'לנסות שוב את הקבצים שנכשלו?' : 'להמשיך את ההרצה?'}
                confirmLabel={mustRetryFailed ? 'ניסיון חוזר' : 'המשך'}
                busy={Boolean(actionJobId)}
                onCancel={() => setConfirming(null)}
                onConfirm={act}
                testId="bulk-detail-confirm-resume"
            >
                <Typography>
                    {mustRetryFailed
                        ? `${formatNumber(counters.failed)} קבצים שנכשלו ינסו ${replacing ? 'להיות מוחלפים' : 'להיווצר'} שוב.`
                        : `ההרצה תמשיך מהקובץ הבא. ממתינים ${formatNumber((counters.pending ?? 0) + (retryFailed ? counters.failed ?? 0 : 0))} קבצים.`}
                    {replacing
                        ? ' קבצים שכבר הוחלפו בהרצה הזאת לא יוחלפו שוב, והגיבוי שלהם נשאר כמו שהוא. לפני כל החלפה נוספת הקובץ הישן מגובה.'
                        : (trial ? ' קבצים שכבר נוצרו בניסוי לא ייווצרו שוב.' : ' קבצים שכבר קיימים לא ייגעו.')}
                    {capValue !== undefined ? ` תקרת העלות תהיה ${formatUsd(capValue)}.` : ` תקרת העלות נשארת ${formatUsd(job?.maxCostUsd)}.`}
                </Typography>
                {actionError && <Alert severity="error" sx={{ mt: 1 }}><Box dir="auto">{formatError(actionError)}</Box></Alert>}
            </ConfirmDialog>
        </Dialog>
    );
});

export default BulkRunJobDetails;
